import { createAsyncThunk } from '@reduxjs/toolkit'
import apiConfigs from 'Api/api-config'
import { ItemResultType, Review, ReviewEntry } from 'redux/thunk/types'
import { getConfigKey, prepareHeaders } from 'utility/Api'

export const saveReportReviews = createAsyncThunk(
  'costObjectReport/saveReportReviews',
  // Declare the type your function argument here:
  async (formData: any): Promise<any> => {
    const headers = new Headers()
    headers.append('Content-Type', 'application/json')
    const baseUrl = apiConfigs[getConfigKey()].apiHost
    const apiPath = apiConfigs[getConfigKey()].paths.apiPath
    const filteredReviews = formData.reportConfig
    const response = await fetch(`${baseUrl}/${apiPath}/reviews/save`, {
      method: 'POST',
      headers: await prepareHeaders(headers),
      body: JSON.stringify({ reportConfig: filteredReviews })
    })
    const responseData = await response.json()

    return responseData
  }
)

const runReport = async (page: any = 1, formData: any, sapResponseData: any): Promise<any> => {
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  const baseUrl = apiConfigs[getConfigKey()].apiHost
  const apiPath = apiConfigs[getConfigKey()].paths.apiPath
  const prepareData = (formData: any): any => {
    const supervisors = formData.supervisors.map((supervisor: any) => {
      return supervisor.id
    })
    const profitCenters = formData.profitCenters.map((profitCenter: any) => {
      return profitCenter.id
    })
    const costObjects = formData.costObjects.map((costObject: any) => {
      return costObject.id
    })
    return {
      fiscalPeriod: formData.fiscalPeriod,
      supervisors,
      profitCenters,
      costObjects
    }
  }
  const requestHeaders = await prepareHeaders(headers)
  const responseLoop = await fetch(`${baseUrl}/${apiPath}/costObjectReport?page=${page as string}`, {
    method: 'POST',
    headers: requestHeaders,
    body: JSON.stringify(prepareData(formData))
  })
  let sapResponseDataLoop
  if (responseLoop.status === 403) {
    const errorData = await responseLoop.json()
    if (/not allowed/i.test(errorData.message || '')) {
      throw new Error('User not allowed to execute RFC')
    }
    sapResponseDataLoop = errorData
  } else {
    sapResponseDataLoop = await responseLoop.json()
  }
  const nextPage = parseInt(sapResponseDataLoop.nextPage)
  sapResponseData.row = sapResponseData.row.concat(sapResponseDataLoop.row)
  if (!isNaN(nextPage) && sapResponseDataLoop.row.length > 0 && sapResponseDataLoop.row[0].TYPE !== 'E') {
    if (nextPage !== 0) {
      await runReport(nextPage, formData, sapResponseData)
    }
  }
  return sapResponseData
}

export const runTransactionTypeReport = createAsyncThunk(
  'transactionTypeReport/runTransactionTypeReport',
  // Declare the type your function argument here:
  async (formData: any): Promise<any> => {
    const headers = new Headers()
    headers.append('Content-Type', 'application/json')
    const baseUrl = apiConfigs[getConfigKey()].apiHost
    const apiPath = apiConfigs[getConfigKey()].paths.apiPath
    const requestHeaders = await prepareHeaders(headers)

    const sapResponseData: any = await runReport(1, formData, { row: [] })

    let error: any

    if (sapResponseData.row[0].MESSAGE !== null && sapResponseData.row[0].MESSAGE !== undefined) {
      error = sapResponseData.row[0].MESSAGE
      return {
        fiscalPeriod: formData.fiscalPeriod,
        results: [],
        error,
        formData
      }
    }
    const finalData: ItemResultType[] = sapResponseData.row.map((data: any) => {
      let coupaPoNumber = data.PO_COUP.split('COUPA')[1]
      if (coupaPoNumber === undefined) {
        coupaPoNumber = data.PO_COUP
      }
      return {
        id: data.BELNR,
        profitCenter: data.PRCTR,
        costObjectName: `${data.COST_OBJECT as string} - ${data.OBJNAME as string}`,
        transactionType: data.TRANS,
        docTypeDescription: data.DOCTYPE,
        glAccount: data.COST_ELEMENT,
        docDate: data.DOCDATE,
        docNumber: data.DOCNR,
        lineItemText: data.DESCR,
        reference: data.REFNR,
        poNumber: data.PO_DOC,
        transactionDetail: data.DETAIL,
        actualAmount: data.AMOUNT,
        reviewed: Review.NONE,
        risk: data.RISK,
        period: data.PERIO_TEXT,
        riskReason: data.REASON,
        coupaPoNumber: coupaPoNumber,
        reason: {
          type: '',
          comment: ''
        },
        expanded: false,
        type: 'MASTER'
      }
    })
    /*
        DATA MIGRATION V2
    */
    let oldIds = ''
    finalData.forEach((result: ItemResultType) => {
      const splitParsedId = result.docNumber.split(',')[0].split('-')
      const parsedId = splitParsedId[0] + '-' + splitParsedId[1]
      oldIds += `${parsedId},`
    })
    const oldReviewResponse = await fetch(`${baseUrl}/${apiPath}/reviews`, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify({
        docIds: oldIds
      })
    })
    oldIds = oldIds.slice(0, -1)

    const oldDocReviews = await oldReviewResponse.json()
    if (oldDocReviews[0].length > 0) {
      oldDocReviews[0].forEach((result: any) => {
        const findObject = finalData.find((data: ItemResultType) => {
          const localDocNum = `${data.docNumber.split(',')[0]}`
          const splitParsedId = localDocNum.split('-')
          const parsedId = splitParsedId[0] + '-' + splitParsedId[1]
          const splitResultId = result.RE_ID.split('-')
          const resultId = splitResultId[0] + '-' + splitResultId[1]

          return parsedId === resultId
        })
        if (findObject != null) {
          findObject.reviewed = result.STATUS
          findObject.reason.type = result.REASON_CODE
          findObject.reason.comment = result.CGCOMMENT
        }
      })
    }
    /*
        ORIGINAL SAVES
    */
    let ids = ''
    finalData.forEach((result: ItemResultType) => {
      const splitParsedId = result.id.split('-')
      const parsedId = splitParsedId[0] + '-' + splitParsedId[1]
      ids += `${parsedId},`
    })
    ids = ids.slice(0, -1)
    const reviewResponse = await fetch(`${baseUrl}/${apiPath}/reviews`, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify({
        docIds: ids
      })
    })
    const docReviews = await reviewResponse.json()
    if (docReviews[0].length > 0) {
      docReviews[0].forEach((result: any) => {
        const findObject = finalData.find((data: ItemResultType) => {
          const localDocNum = `${data.id}`
          const splitParsedId = localDocNum.split('-')
          const parsedId = splitParsedId[0] + '-' + splitParsedId[1]
          const splitResultId = result.RE_ID.split('-')
          const resultId = splitResultId[0] + '-' + splitResultId[1]

          return parsedId === resultId
        })
        if (findObject != null) {
          findObject.reviewed = result.STATUS
          findObject.reason.type = result.REASON_CODE
          findObject.reason.comment = result.CGCOMMENT
        }
      })
    }
    /*
            DATA MIGRATION
        */
    let newIds = ''
    finalData.forEach((result: ItemResultType) => {
      const splitParsedId = `${result.docNumber}-${result.id.split('-')[1]}`.split('-')
      const parsedId = splitParsedId[0] + '-' + splitParsedId[1]
      newIds += `${parsedId},`
    })
    const newReviewResponse = await fetch(`${baseUrl}/${apiPath}/reviews`, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify({
        docIds: newIds
      })
    })
    newIds = newIds.slice(0, -1)

    const newDocReviews = await newReviewResponse.json()
    if (newDocReviews[0].length > 0) {
      newDocReviews[0].forEach((result: any) => {
        const findObject = finalData.find((data: ItemResultType) => {
          const localDocNum = `${data.docNumber.split(',')[0]}`
          const splitParsedId = localDocNum.split('-')
          const parsedId = splitParsedId[0] + '-' + splitParsedId[1]
          const splitResultId = result.RE_ID.split('-')
          const resultId = splitResultId[0] + '-' + splitResultId[1]

          return parsedId === resultId
        })
        if (findObject != null) {
          findObject.reviewed = result.STATUS
          findObject.reason.type = result.REASON_CODE
          findObject.reason.comment = result.CGCOMMENT
        }
      })
    }

    // Inferred return type: Promise<MyData>
    return {
      fiscalPeriod: formData.fiscalPeriod,
      results: finalData,
      formData
    }
  }
)

export const clearTransactionTypeReport = createAsyncThunk(
  'transactionTypeReport/clearTransactionTypeReport',
  // Declare the type your function argument here:
  async () => {
    // const response = await fetch(``)
    // // Inferred return type: Promise<MyData>
    // return (await response.json()) as TransactionTypeReportResult
    return []
  }
)
